// The artist TEAM's side of a show request (ART-327) — the WRAPPER layer. Mgmt and the artist AV (`booking:decide`)
// read what the agent sent and answer it: Approve, Hold & add crew, or Pass. The first answer wins; a later one gets
// a 409 naming who answered, which these hooks hand back as data rather than as an error.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type ShowRequestView = components["schemas"]["ShowRequestView"];
export type ShowRequestAlreadyDecided = components["schemas"]["ShowRequestAlreadyDecided"];
export type HoldInvitePerson = components["schemas"]["HoldInvitePerson"];
export type ShowRequestAction = "approve" | "hold" | "pass";

/** `GET …/booking/requests` — requests waiting on the team, plus ones answered in the last 30 days. */
export function useShowRequests(opts: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useBandQuery<components["schemas"]["ShowRequestsResponse"]>(
    ["booking", "requests"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/booking/requests", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled: opts.enabled ?? true },
  );
}

/** `GET …/booking/requests/{offerId}` — one request, for its Details. */
export function useShowRequest(offerId: string | null) {
  const { api } = useApp();
  return useBandQuery<ShowRequestView>(
    ["booking", "requests", offerId ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/booking/requests/{offerId}", {
        params: { path: { bandId, offerId: offerId ?? "" } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: !!offerId, staleTime: 15_000 },
  );
}

/** What an answer came back as: the answered request, or who answered first. */
export type DecisionResult = { decided: ShowRequestView; conflict?: undefined } | { decided?: undefined; conflict: ShowRequestAlreadyDecided };

function useInvalidateBooking() {
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return () => {
    for (const key of ["booking", "needs-attention", "ops-calendar", "shows", "show-invites"]) {
      void qc.invalidateQueries({ queryKey: ["band", bandId, key] });
    }
  };
}

/** `POST …/booking/requests/{offerId}/decision` — approve | hold | pass (with an optional reason). */
export function useDecideShowRequest() {
  const { api } = useApp();
  const invalidate = useInvalidateBooking();
  return useBandMutation<DecisionResult, { offerId: string; action: ShowRequestAction; reason?: string | null }>(
    async (band, { offerId, action, reason }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/booking/requests/{offerId}/decision",
        { params: { path: { bandId: band, offerId } }, body: { action, reason: reason ?? null } },
      );
      if (response.status === 409) return { conflict: error as unknown as ShowRequestAlreadyDecided };
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return { decided: data as ShowRequestView };
    },
    {
      onSuccess: invalidate,
      onError: (err) => pushToast(errorMessage(err, "Couldn't save your answer")),
    },
  );
}

/** `POST …/shows/{showId}/hold-invites` — "Who's playing": the people, their pay, and one note to the team. */
export function useSendHoldInvites() {
  const { api } = useApp();
  const invalidate = useInvalidateBooking();
  return useBandMutation<void, { showId: string; people: HoldInvitePerson[]; note?: string | null }>(
    async (band, { showId, people, note }) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/shows/{showId}/hold-invites", {
        params: { path: { bandId: band, showId } },
        body: { people, note: note ?? null },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: invalidate,
      onError: (err) => pushToast(errorMessage(err, "Couldn't send the hold invites")),
    },
  );
}
